import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ElementRef, EventEmitter, HostListener, Input,
  NgZone,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { fabric } from 'fabric';
import { Canvas, Ellipse, IBaseFilter, Line, Point, Rect, Text } from 'fabric/fabric-impl';
import { InspectionImage } from '../../../_objects/inspection-image';
import { InspectionImageService } from '../../../_services/http-api/inspection-image.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddTextComponent } from '../add-text/add-text.component';
import { MARKCOLORITEMS, ANNOTATION_COLOR_ITEMS, USE_IMAGE_TYPE } from '../../../app.constants';
import { CommonConfirmDialogComponent } from '../../../common-dialog/common-confirm-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { ImageTrimComponent } from '../../../_shared/component/image-trim/image-trim.component';
import { SpinnerService } from '../../../_services/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS, RetouchSettingsQueryParam, StorageService } from '../../../_services/storage.service';
import { BlobService } from '../../../_services/blob.service';
import { IconEnum, IconType } from "@evc/web-components";

export class AnnotationEmitData {
  modifiedImage: Blob;
  annotationObjects: any;
  measurementObjects: any;
  initDataJson: any;
  isDisable: boolean;

  constructor(modifiedImage: Blob, annotationObjects: any, measurementObjects: any, initDataJson: any, isDisable: boolean) {
    this.modifiedImage = modifiedImage;
    this.annotationObjects = annotationObjects;
    this.measurementObjects = measurementObjects;
    this.initDataJson = initDataJson;
    this.isDisable = isDisable;
  }
}

@Component({
    selector: 'app-image-annotation-edit',
    templateUrl: './image-annotation-edit.component.html',
    styleUrls: ['./image-annotation-edit.component.scss'],
    standalone: false
})
export class ImageAnnotationEditComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() inspectionImage: InspectionImage;
  @Input() annotationObjects: any = null;
  @Input() isDataLoaded = false;
  @Input() isEditable: boolean;
  @Output() modified = new EventEmitter<AnnotationEmitData>();

  squareIcon = IconEnum.Square;
  readonly TRIM_HEIGHT = 800;
  readonly TRIM_WIDTH = 800;
  readonly DUMMY = 'dummy';
  readonly MAIN = 'main';
  readonly MAX_HISTORIES = 1000;
  readonly borderTypeList: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 30, 50, 100];
  readonly fillPercentageList = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  readonly opacityColorCodes = new Map<number, string>([
    [0, '00'],
    [10, '1A'],
    [20, '33'],
    [30, '4D'],
    [40, '66'],
    [50, '80'],
    [60, '99'],
    [70, 'B3'],
    [80, 'CC'],
    [90, 'E6'],
    [100, 'FF']
  ]);
  readonly selectColors: MARKCOLORITEMS[] = ANNOTATION_COLOR_ITEMS;
  readonly editFunctions = [this.translate.instant('imageAnnotationEdit.annotation'), this.translate.instant('imageAnnotationEdit.imageRetouch')];
  readonly maxBrightness = 1;
  readonly minBrightness = -1;

  public canvas: Canvas;
  public dummyCanvas: Canvas;
  public cropCanvas: Canvas;
  public imageSrc: string;
  public cropImageSrc: string;
  public imgSrcBase64: string;
  private rectangle = new fabric.Rect({
    fill: 'transparent',
    stroke: '#ccc',
    strokeDashArray: [2, 2],
    visible: false
  });
  public histories = [];
  public activeHistoryIndex = -1;
  public fillOpacity = 0;
  public fillColor = '#ffffff';
  public isFilled = false;
  public borderType = 6;
  public borderColor = '#000000';
  public initLoaded = false;
  public initDataJson: string;
  public selectFunction: string;
  public brightness = 0.00;
  public isFormatEditable = false;
  public isBorderEditable = true;
  public isCanvasLoading = true;
  public scale = 1;
  public baseImageWidth: number;
  public baseImageHeight: number;
  public baseImage: HTMLImageElement;
  private firstActiveObjIndex = -1;
  private baseMainImage: fabric.Image;
  private onDestroy$ = new Subject();
  public resizeTimer = null;
  public mainContent: HTMLElement;

  get RETOUCH_SETTINGS_QUERY_PARAM(): RetouchSettingsQueryParam {
    return {
      fill: {
        isFill: this.isFilled,
        color: this.fillColor
      },
      border: {
        strokeWidth: this.borderType,
        color: this.borderColor
      }
    };
  }

  get isAnnotationFunc() {
    return this.selectFunction === this.editFunctions[0];
  }

  @ViewChild('GenImag') imgEl: ElementRef;

  constructor(
    private cd: ChangeDetectorRef,
    private overlay: Overlay,
    private router: Router,
    private inspectionImageService: InspectionImageService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private blobService: BlobService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private storageService: StorageService,
    private zone: NgZone
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      this.editFunctions[0] = this.translate.instant('imageAnnotationEdit.annotation');
      this.editFunctions[1] = this.translate.instant('imageAnnotationEdit.imageRetouch');
    });
    this.selectFunction = this.editFunctions[0];

    // Session Storageに設定情報が保存されている場合、"Object Style"に反映
    const param = this.storageService.fetchRetouchSettingQueryParam(INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS);
    if (param) {
      this.isFilled = param.fill.isFill;
      this.fillColor = param.fill.color;
      this.borderType = param.border.strokeWidth;
      this.borderColor = param.border.color;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isDataLoaded && this.isDataLoaded) {
      if (this.inspectionImage && !this.initLoaded) {
        this.initLoaded = true;
        this.oscSpinnerService.show(true);
        this.loadImage();
      }
    }
  }

  ngOnInit(): void {
    this.mainContent = document.getElementById('annotation-main-content');
  }
  ngAfterViewInit() {
    setTimeout(() =>this.onResize(), 1000);
  }
  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    clearTimeout(this.resizeTimer);

    // "Object Style"情報をSession Storageに保存
    this.storageService.pushRetouchSettingQueryParam(INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS, this.RETOUCH_SETTINGS_QUERY_PARAM);
  }

  changeMode(mode: string) {
    if (mode == this.editFunctions[1]) {
      this.canvas.isDrawingMode = false;
      this.canvas.discardActiveObject();
      this.canvas.renderAll();
    }

    this.selectFunction = mode;
    this.cd.detectChanges();
  }

  async loadImage(): Promise<void> {
    // 画像URLを取得
    const imageType
      = this.inspectionImage.hasMeasurementImage ? USE_IMAGE_TYPE.REMEASUREMENT : USE_IMAGE_TYPE.ORIGINAL;
    this.imageSrc = await this.inspectionImageService
      .getImageBinaryUrl(this.inspectionImage.inspectionId, this.inspectionImage.id, imageType);

    // URLから画像をbase64形式で読み取る
    await this.blobService.getBase64ImageFromUrl(this.imageSrc)
      .then(base64 => {
        const image = new Image();
        this.imgSrcBase64 = base64;
        image.src = base64;
        image.onload = () => this.setupCropCanvas(image);
      })
      .catch(err => {
        this.oscSpinnerService.hide(true);
        console.error(err);
      }
      );
  }

  @HostListener('window:resize')
  onResize() {
    const container = document.querySelector('#canvas-area');
    this.canvas.setWidth(container.clientWidth);
    this.canvas.setHeight(container.clientHeight); // Optional: Adjust height too
    this.canvas.renderAll();
  }
  /** canvasの設定
   * @returns void
   */
  setupCanvas(image: HTMLImageElement): void {
    this.scale = this.getScale();
    const canvasConfig = {
      width: image.width * this.scale,
      height:  image.height * this.scale,
      border: 'solid'
    };

    const dummyCanvasConfig = {
      width: image.width,
      height: image.height,
      border: 'solid'
    };

    // 保存用Canvasの設定
    this.dummyCanvas = new fabric.Canvas('dummyCanvas', dummyCanvasConfig);
    this.dummyCanvas.preserveObjectStacking = true; // クリックされた時に図形の順番を変更しない
    this.addImage(dummyCanvasConfig.width / 2, dummyCanvasConfig.height / 2, 1, image.src, this.DUMMY);

    // 操作・表示用Canvasの設定
    this.canvas = new fabric.Canvas('canvas', canvasConfig);
    this.canvas.preserveObjectStacking = true; // クリックされた時に図形の順番を変更しない
    this.canvas.freeDrawingBrush.width = this.borderType; // ボーダーのサイズ設定
    this.canvas.freeDrawingBrush.color = this.borderColor; // ボーダーの色設定
    this.canvas.hoverCursor = 'default';
    this.canvas.isDrawingMode = false;
    this.addImage(canvasConfig.width / 2, canvasConfig.height / 2, this.scale, image.src, this.MAIN);
    this.onResize();
  }

  /** canvasに画像を配置する */
  public addImage(left: number, top: number, scale: number, path: string, canvas: string, isResize: boolean = false): void {
    this.isCanvasLoading = true;
    const imageConfig = {
      top: top,
      left: left,
      originX: 'center',
      originY: 'center',
      scaleX: scale,
      scaleY: scale
    };

    if (canvas === this.DUMMY) {
      fabric.Image.fromURL(path, (img) => {
        img.selectable = false;
        this.setBrightness(img, 0);
        this.dummyCanvas.add(img);
      }, imageConfig);
    } else {
      fabric.Image.fromURL(path, (img) => {
        img.selectable = false;
        this.baseMainImage = img;
        this.setBrightness(img, 0);
        this.canvas.add(img);
        // Image配置直後の状態を保存する
        if (!isResize) {
          this.initDataJson = JSON.parse(this.getPreciseJson());
        }
        if (isResize) {
          this.syncCanvasData(true);
          this.setCanvasAngle();
          this.setCanvasEvent();
        } else if (this.annotationObjects?.modifiedItems) {
          // 既に更新済みのアノテーションがある場合上書きする
          const canvasData = JSON.parse(JSON.stringify(this.initDataJson));

          // canvasデータのobject情報を書き換える
          canvasData.objects = this.annotationObjects.modifiedItems;
          if (!this.isTrimmed(canvasData.objects[0])) {
            canvasData.objects[0].src = this.imgSrcBase64; // 過去のsrc情報を更新する
          }

          this.loadJsonToCanvas(canvasData, true);
        } else {
          this.setCanvasEvent();
          this.isCanvasLoading = false;
          this.emitModifiedData();
          this.addHistory();
        }
        this.oscSpinnerService.hide(true);
        this.zone.run(() => {
          // レンダリング後に何かしらのイベントを走らせないと計算した値でのUI表示ができない
          const event = new KeyboardEvent('keydown', { key: 'Enter' });
          document.getElementById('annotation-main-content').dispatchEvent(event);
        });
      }, imageConfig);
    }
    this.addResizeEvent();
  }

  setCanvasEvent() {
    this.canvas.on('object:added', (e) => this.changedObjects());
    this.canvas.on('object:modified', (e) => this.changedObjects());
    this.canvas.on('selection:created', (e) => this.setObjectEditor());
    this.canvas.on('selection:updated', (e) => this.setObjectEditor());
    this.canvas.on('selection:cleared', (e) => this.firstActiveObjIndex = -1);
  }

  removeCanvasEvent() {
    this.canvas.off('object:added');
    this.canvas.off('object:modified');
    this.canvas.off('selection:created');
    this.canvas.off('selection:updated');
    this.canvas.off('selection:cleared');
  }

  /** canvasに四角形オブジェクトを配置する */
  public addRect(): void {
    if (this.isCanvasLoading) { return; }

    const obj = this.generateRect(100 * this.scale, 100 * this.scale, 100 * this.scale, 100 * this.scale, this.getFillColor(), this.borderType, this.borderColor);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  /** canvasに円形オブジェクトを配置する */
  public addCircle(): void {
    if (this.isCanvasLoading) { return; }

    const obj = this.generateCircle(100 * this.scale, 100 * this.scale, 50 * this.scale, 50 * this.scale, this.getFillColor(), this.borderType, this.borderColor);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  public addText(): void {
    if (this.isCanvasLoading) { return; }

    const dialogRef = this.dialog.open(AddTextComponent, {
      width: '450px',
      disableClose: true,
    });

    dialogRef.componentInstance.addEmitter.subscribe(text => {
      const obj = this.generateText(100 * this.scale, 100 * this.scale, 50 * this.scale, text, this.borderColor, 100 * this.scale);
      this.canvas.add(obj);

      const objects = this.canvas.getObjects();
      const activateObj = objects.find((o, i) => i === objects.length - 1);
      this.canvas.setActiveObject(activateObj);
      this.canvas.isDrawingMode = false;
      this.canvas.renderAll();
      dialogRef.close();
    });
  }

  public addLine(): void {
    if (this.isCanvasLoading) { return; }

    const obj = this.generateLine(100 * this.scale, 100 * this.scale, 280 * this.scale, 100 * this.scale, 6 * this.scale, this.borderColor);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  // 矢印を追加
  public addAllow(): void {
    if (this.isCanvasLoading) { return; }

    const obj = this.generateText(100 * this.scale, 100 * this.scale, 50 * this.scale, '→', this.borderColor, 100 * this.scale);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  public toggleFreeHand(isFreehand: boolean): void {
    this.changeMode(this.editFunctions[0]);

    if (this.isCanvasLoading) { return; }

    this.canvas.isDrawingMode = isFreehand;
  }

  public changeLayer(isUp: boolean): void {
    const activeObjects = this.canvas.getActiveObjects();

    if (isUp) {
      // 上位位レイヤーから処理する
      activeObjects.reverse().forEach(obj => {
        const zIndex = this.canvas._objects.indexOf(obj);
        if (zIndex > 0) {
          this.canvas.moveTo(obj, zIndex + 1);
        }
      });
    } else {
      // 下位レイヤーから処理する
      activeObjects.forEach(obj => {
        const zIndex = this.canvas._objects.indexOf(obj);
        if (zIndex > 1) {
          this.canvas.moveTo(obj, zIndex - 1);
        }
      });
    }

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeLayerToTop(): void {
    const activeObjects = this.canvas.getActiveObjects();

    // 下位レイヤーから処理する
    activeObjects.forEach(obj => {
      const zIndex = this.canvas._objects.indexOf(obj);
      if (zIndex > 0) {
        this.canvas.moveTo(obj, this.canvas._objects.length - 1);
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeLayerToBottom(): void {
    const activeObjects = this.canvas.getActiveObjects();

    // 上位位レイヤーから処理する
    activeObjects.reverse()
      .forEach(obj => {
        const zIndex = this.canvas._objects.indexOf(obj);
        if (zIndex > 0) {
          this.canvas.moveTo(obj, 1);
        }
      });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public removeObject(): void {
    const activeObjects = this.canvas.getActiveObjects();
    activeObjects.forEach(obj => {
      if (obj.type !== 'image') {
        this.canvas.remove(obj);
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
    this.firstActiveObjIndex = -1;
  }

  public isActiveObject(): boolean {
    return this.canvas
      && this.canvas._objects
      && this.canvas._objects.length > 1
      && this.canvas.getActiveObjects().length > 0;
  }

  public changeFillColor(): void {
    this.canvas
      .getActiveObjects()
      .forEach(obj => {
        if (obj.type === 'rect' || obj.type === 'ellipse') {
          obj.set({ 'fill': this.getFillColor() });
        }
      });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBorderColor(): void {
    this.canvas
      .getActiveObjects()
      .forEach(obj => {
        if (obj.type === 'text') {
          obj.set({ 'fill': this.borderColor });
        } else {
          obj.set({ 'stroke': this.borderColor });
        }
      });
    this.canvas.freeDrawingBrush.color = this.borderColor;

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBorderType() {
    this.canvas
      .getActiveObjects()
      .forEach(obj => {
        if (obj.type === 'text') {
          (obj as fabric.Text).set({ 'fontSize': this.borderType });
        } else {
          obj.set({ 'strokeWidth': this.borderType });
        }
      });

    const width = this.borderType >= 1 ? this.borderType : 1;
    this.canvas.freeDrawingBrush.width = width;

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBrightness(brightness: number, isDetermined = false) {
    this.brightness = brightness;
    this.canvas.getObjects()
      .forEach(obj => {
        if (obj.type === 'image') {
          this.setBrightness((obj as fabric.Image), this.brightness);
        }
      });
    this.canvas.renderAll();

    if (isDetermined) {
      this.syncCanvasData(false);
    }
  }

  public async rotate(angle: number, data: any, isFirstLoad: boolean) {
    const mainObjects = this.canvas.getObjects();
    const dummyObjects = this.dummyCanvas.getObjects();
    if (mainObjects.length > 1 && isFirstLoad) {
      // アノテーションが存在する場合、アノテーション削除確認をする
      const dialogRef = this.openFlushConfirmDialog();
      const pressOK = await dialogRef.afterClosed()
        .toPromise()
        .then(result => result);

      if (!pressOK) { return; }

      // 画像以外削除
      this.canvas._objects = mainObjects.filter((o, i) => i === 0);
      this.dummyCanvas._objects = dummyObjects.filter((o, i) => i === 0);
    }

    const mainImage = this.canvas.getObjects()[0];
    const dummyImage = this.dummyCanvas.getObjects()[0];
    const sumAngle = mainImage.angle + angle;

    // 360度を越えるようであれば0にする
    mainImage.angle = Math.abs(sumAngle % 360)
      ? sumAngle
      : 0;
    dummyImage.angle = Math.abs(sumAngle % 360)
      ? sumAngle
      : 0;

    this.setCanvasAngle();
    this.canvas.discardActiveObject();
    this.dummyCanvas.discardActiveObject();
    this.renderAll();

    if (data) {
      this.addAnnotation(data);
    } else {
      this.addHistory();
    }
    this.emitModifiedData();
  }

  public addHistory() {
    // 通常の場合
    if (this.activeHistoryIndex < this.MAX_HISTORIES) {
      this.activeHistoryIndex++;
      // splice 第一引数から第二引数文削除し、第三引数以降を挿入する
      this.histories = this.histories.filter((v, i) => i < this.activeHistoryIndex);
      this.histories.push(this.getPreciseJson());
    } else { // 最大数持っている場合
      this.histories.shift();
      this.histories.push(this.getPreciseJson());
    }
  }

  public undo() {
    if (!this.isCanvasLoading && this.activeHistoryIndex > 0) {
      this.activeHistoryIndex--;
      this.canvas._objects = this.canvas._objects.filter(obj => obj.type === 'image');
      this.loadJsonToCanvas(JSON.parse(this.histories[this.activeHistoryIndex]));
    }
  }

  public redo() {
    if (!this.isCanvasLoading && this.activeHistoryIndex < this.histories.length - 1) {
      this.activeHistoryIndex++;
      this.canvas._objects = this.canvas._objects.filter(obj => obj.type === 'image');
      this.loadJsonToCanvas(JSON.parse(this.histories[this.activeHistoryIndex]));
    }
  }

  public getColorName(colorCode: string): string {
    const color = this.selectColors.find(_ => _.color === colorCode);
    return color
      ? this.translate.instant('imageAnnotationEdit.annotation_color_' + color.name.toLowerCase())
      : '';
  }

  public formatBrightnessLabel(value: number) {
    return Math.floor(value * 100);
  }
  public async showTrimCanvas(): Promise<void> {
    const objects = this.canvas.getObjects();
    let executeFlush = false;
    if (objects.length > 1) {
      // アノテーションが存在する場合、アノテーション削除確認をする
      const flushDialogRef = this.openFlushConfirmDialog();
      executeFlush = await flushDialogRef.afterClosed()
        .toPromise()
        .then(result => result);
    }

    const trimDialogRef = this.openImageTrimDialog();
    trimDialogRef.componentInstance.closePanel
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(info => {
        if (executeFlush) {
          // 画像以外削除
          this.canvas._objects = objects.filter((o, i) => i === 0);
          this.canvas.discardActiveObject();
        }

        if (info) {
          this.updateMainImageSrc(info[0], info[1], 0, false, false);
          this.rectangle = info[1];
        }

        trimDialogRef.close();
      }
      );
  }

  public async revertTrim(): Promise<void> {
    const objects = this.canvas.getObjects();
    if (objects.length > 1) {
      // アノテーションが存在する場合、アノテーション削除確認をする
      const dialogRef = this.openFlushConfirmDialog();
      const pressOK = await dialogRef.afterClosed()
        .toPromise()
        .then(result => result);

      if (!pressOK) { return; }

      // 画像以外削除
      this.canvas._objects = objects.filter((o, i) => i === 0);
      this.dummyCanvas._objects = objects.filter((o, i) => i === 0);
    }

    this.updateMainImageSrc(this.imgSrcBase64, null, 0, false, true);
  }

  private loadJsonToCanvas(json: any, isAddHistory: boolean = false) {
    this.isCanvasLoading = true;
    this.removeCanvasEvent();

    // 選択解除し、非選択状態にする。
    this.canvas.discardActiveObject();
    this.canvas._objects.forEach(obj => obj.set({ selectable: false }));
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();

    // 画像表示用にSrcを設定
    const jsonData = isAddHistory ? this.getScaleCorrectionJsonData(json) : json;
    const data = JSON.parse(JSON.stringify(jsonData));
    data.objects.some((obj, i) => {
      if (obj.type === 'image' && i === 0) {
        obj.src = this.imgSrcBase64;
        return true;
      }
      return false;
    });

    const image = data.objects.find(obj => obj.type === 'image');
    if (image.data && image.data[0] && image.data[0].width) {
      this.rectangle.width = image.data[0].width;
      this.rectangle.height = image.data[0].height;
      this.rectangle.left = image.data[0].left;
      this.rectangle.top = image.data[0].top;
      this.baseImageWidth = image.data[0].width;
      this.baseImageHeight = image.data[0].height;
      this.scale = this.getScale();

      // 輝度の取得
      if (image.filters.length > 0) {
        image.filters.some((filter: any) => {
          if (filter.hasOwnProperty('type')
            && filter.type === 'Brightness'
            && filter.hasOwnProperty('brightness')) {
            this.brightness = filter.brightness;
            return true;
          }
        });
      }

      // 輝度の反映
      this.dummyCanvas.getObjects().forEach(obj => {
        if (obj.type === 'image') {
          this.setBrightness((obj as fabric.Image), this.brightness);
        }
      });

      this.crop(image.angle, data, isAddHistory);

      this.setCanvasEvent();
      this.canvas.renderAll();

      if (isAddHistory) {
        this.addHistory();
      }

      this.syncCanvasData(true);
      this.isCanvasLoading = false;
    } else {
      this.baseImageWidth = image.width;
      this.baseImageHeight = image.height;
      this.scale = this.getScale();
      this.dummyCanvas.loadFromJSON(data, () => {
        const image = this.dummyCanvas.getObjects().find(_ => _.type === 'image') as fabric.Image;

        image.set({ scaleX: 1, scaleY: 1 });
        this.dummyCanvas.renderAll();
      });
      this.canvas.loadFromJSON(data, () => {
        const image = this.canvas.getObjects().find(_ => _.type === 'image') as fabric.Image;
        image.set({ scaleX: this.scale, scaleY: this.scale });
        if (this.isEditable) {
          // 最背面の画像のみ選択不可とする。
          image.set({ selectable: false });
        } else {
          // 全て選択不可とする
          this.canvas._objects.forEach(obj => obj.set({ selectable: false }));
        }

        // 輝度の反映
        image.applyFilters();

        // 輝度の取得
        if (image.filters.length > 0) {
          image.filters.some((filter: any) => {
            if (filter.hasOwnProperty('type')
              && filter.type === 'Brightness'
              && filter.hasOwnProperty('brightness')) {
              this.brightness = filter.brightness;
              return true;
            }
          });
        }

        this.setCanvasAngle();

        if (isAddHistory) {
          this.addHistory();
        }

        this.syncCanvasData(true);
        this.setCanvasEvent();
        this.isCanvasLoading = false;
      });
    }
  }

  /** 四角形オブジェクトを作成する */
  private generateRect(left: number, top: number, width: number, height: number,
    color: string, strokeWidth: number, stroke: string, globalCompositeOperation = null): Rect {
    const obj = new fabric.Rect({
      left: left,
      top: top,
      width: width,
      height: height,
      fill: color,
      strokeWidth: strokeWidth,
      stroke: stroke,
      globalCompositeOperation: globalCompositeOperation
    });

    return obj;
  }

  private generateCircle(left: number, top: number, rx: number, ry: number, color: string, strokeWidth: number, stroke: string): Ellipse {
    const obj = new fabric.Ellipse({
      left: left,
      top: top,
      rx: rx,
      ry: ry,
      fill: color,
      strokeWidth: strokeWidth,
      stroke: stroke
    });

    return obj;
  }

  private generateText(left: number, top: number, width: number, text: string, color: string, fontSize: number): Text {
    const obj = new fabric.Text(text, {
      left: left,
      top: top,
      fill: color,
      fontSize: fontSize,
      width: width
    });
    return obj;
  }

  private generateLine(left: number, top: number, endX: number, endY: number, strokeWidth: number, stroke: string): Line {
    const obj = new fabric.Line([left, top, endX, endY], {
      stroke: stroke,
      strokeWidth: strokeWidth
    }
    );

    return obj;
  }

  get OverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: // 表示位置に関する設定
        this.overlay.position()
          .global()
          .top()
          .left(),
      hasBackdrop: true, // 背景の表示
      backdropClass: ['custom-overlay'], // 背景に指定するクラス
      disposeOnNavigation: true // ナビゲーション時に削除
    });
  }

  get isUndoDisabled() {
    return !this.activeHistoryIndex || this.activeHistoryIndex < 1;
  }

  get isRedoDisabled() {
    return this.activeHistoryIndex >= this.histories.length - 1;
  }

  private emitModifiedData() {
    const canvasJson = this.dummyCanvas.toDatalessJSON(['data']);

    // 初期状態と比較
    if (JSON.stringify(canvasJson) !== JSON.stringify(this.initDataJson)) {
      // 変更が加わっているのであれば更新する
      const jsonData = this.getPreciseJson();
      const modifiedData = JSON.parse(jsonData);
      modifiedData.objects.some((obj, i) => {
        if (obj.type === 'image' && i === 0) {
          obj.src = '';
          return true;
        }
        return false;
      });

      if (modifiedData.objects[0] && !modifiedData.objects[0].data) {
        modifiedData.objects[0].data = [null, { isNewSpecification: true }]; // v2.0.20以降に計測があったかどうか
      }

      const base64Data = this.dummyCanvas.toDataURL({
        format: 'jpeg',
        quality: 1
      });
      const blob = this.toBlob(base64Data);

      const result = new AnnotationEmitData(blob, modifiedData.objects, null, this.initDataJson, false);
      this.modified.emit(result);
    } else {
      // 変更が加わっていなければオブジェクトをnullで更新する。
      const result = new AnnotationEmitData(null, null, null, this.initDataJson, false);
      this.modified.emit(result);
    }
  }

  getSelectedIconStyle(isSelected: boolean): object {
    const style = {};
    if (isSelected) {
      style['background-color'] = '#08107b';
      style['opacity'] = 0.6;
      style['border-radius'] = 0;
    }
    return style;
  }

  private changedObjects() {
    this.syncCanvasData(false);
  }

  addAnnotation(data: any) {
    this.canvas._objects = this.canvas._objects.filter(obj => obj.type === 'image');
    const scale = 1 / this.scale;
    data.objects.forEach(_ => {
      let obj;
      if (_.type === 'image') { /* empty */ }
      else if (_.type === 'rect') {
        // Rectアノテーションの作成
        obj = this.generateRect(_.left / scale, _.top / scale, _.width / scale, _.height / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'ellipse') {
        // Circleアノテーションの作成
        obj = this.generateCircle(_.left / scale, _.top / scale, _.rx / scale, _.ry / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'line') {
        // Lineアノテーションの作成
        obj = this.generateLine(_.left / scale, _.top / scale, (_.left + _.width) / scale, (_.top + _.height) / scale, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'text' && _.text === '→') {
        // Arrowアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, '→', _.fill, _.fontSize / scale);
      } else if (_.type === 'text') {
        // Textアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, _.text, _.fill, _.fontSize / scale);
      } else if (_.path) {
        // FreeHandアノテーションの作成
        const path = _.path.map(item => {
          if (item[0] === 'M' || item[0] === 'L') {
            return [
              item[0],
              item[1] / scale,
              item[2] / scale
            ];
          } else {
            return [
              item[0],
              item[1] / scale,
              item[2] / scale,
              item[3] / scale,
              item[4] / scale
            ];
          }
        }) as Point[];
        obj = new fabric.Path(path);
        obj.fill = null;
        obj.stroke = _.stroke;
        obj.strokeWidth = _.strokeWidth / scale;
        obj.top = _.top / scale;
        obj.left = _.left / scale;
        obj.width = _.width / scale;
        obj.height = _.height / scale;
        obj.strokeLineCap = _.strokeLineCap;
        obj.strokeLineJoin = _.strokeLineJoin;
        obj.strokeMiterLimit = _.strokeMiterLimit;
      }

      if (obj && _.type !== 'image') {
        obj.scaleX = _.scaleX;
        obj.scaleY = _.scaleY;
        obj.angle = _.angle;
        this.canvas.add(obj);
      }
      this.canvas.renderAll();
    });
    this.syncCanvasData(false);
    this.emitModifiedData();
  }

  private syncCanvasData(isMain: boolean) {
    if (this.dummyCanvas._objects.length <= 0 && this.canvas._objects.length <= 0) { return; }
    let img: any;
    let scale: number;
    let canvas: any;
    let syncedCanvas: any;
    if (isMain) {
      // dummyCanvasのデータをcanvasにSyncする
      img = this.canvas._objects[0];
      scale = 1 / this.scale;
      this.canvas._objects = [];
      canvas = this.dummyCanvas.getObjects();
      syncedCanvas = this.canvas;
    } else {
      // canvasのデータをdummyCanvasにSyncする
      img = this.dummyCanvas._objects[0];
      scale = this.isTrimmed() ? this.canvas._objects[0].scaleX : this.scale;
      this.dummyCanvas._objects = [];
      canvas = this.canvas.getObjects();
      syncedCanvas = this.dummyCanvas;
    }

    canvas.forEach((_: any) => {
      let obj;
      if (_.type === 'image') {
        obj = img;
        obj.filters = _.filters;
        obj.angle = _.angle;
        obj.applyFilters();
        if (obj.data) {
          obj.data = _.data;
        }
      } else if (_.type === 'rect') {
        // Rectアノテーションの作成
        obj = this.generateRect(_.left / scale, _.top / scale, _.width / scale, _.height / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'ellipse') {
        // Circleアノテーションの作成
        obj = this.generateCircle(_.left / scale, _.top / scale, _.rx / scale, _.ry / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'line') {
        // Lineアノテーションの作成
        obj = this.generateLine(_.left / scale, _.top / scale, (_.left + _.width) / scale, (_.top + _.height) / scale, _.strokeWidth / scale, _.stroke);
      } else if (_.type === 'text' && _.text === '→') {
        // Arrowアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, '→', _.fill, _.fontSize / scale);
      } else if (_.type === 'text') {
        // Textアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, _.text, _.fill, _.fontSize / scale);
      } else if (_.path) {
        // FreeHandアノテーションの作成
        const path = _.path.map(item => {
          if (item[0] === 'M' || item[0] === 'L') {
            return [
              item[0],
              item[1] / scale,
              item[2] / scale
            ];
          } else {
            return [
              item[0],
              item[1] / scale,
              item[2] / scale,
              item[3] / scale,
              item[4] / scale
            ];
          }
        }) as Point[];
        obj = new fabric.Path(path);
        obj.fill = null;
        obj.stroke = _.stroke;
        obj.strokeWidth = _.strokeWidth / scale;
        obj.top = _.top / scale;
        obj.left = _.left / scale;
        obj.width = _.width / scale;
        obj.height = _.height / scale;
        obj.strokeLineCap = _.strokeLineCap;
        obj.strokeLineJoin = _.strokeLineJoin;
        obj.strokeMiterLimit = _.strokeMiterLimit;
      }

      if (obj && _.type !== 'image') {
        obj.scaleX = _.scaleX;
        obj.scaleY = _.scaleY;
        obj.angle = _.angle;
      }

      syncedCanvas.add(obj);
    });
    this.renderAll();

    if (!isMain) {
      this.addHistory();
    }

    this.isCanvasLoading = false;
    this.emitModifiedData();
  }

  // JSONの背景画像の精度を上げる
  // この処理をしないと、スケールの小数精度が第2位までとなってしまう
  private getPreciseJson() {
    const json = this.dummyCanvas.toDatalessJSON(['data']);
    const data = JSON.parse(JSON.stringify(json));
    data.objects.some((obj, i) => {
      if (obj.type === 'image' && i === 0) {
        obj.scaleX = obj.scaleY = 1;
        obj.src = '';
        return true;
      }
      return false;
    });

    return JSON.stringify(data);
  }

  private setObjectEditor() {
    const canvasObjects = this.canvas._objects;
    const activeObjects = this.canvas.getActiveObjects();
    let activeObj: fabric.Object;
    if (this.firstActiveObjIndex > 0) {
      const beforeObj = canvasObjects[this.firstActiveObjIndex];

      if (activeObjects.indexOf(beforeObj) > 0) {
        // 前回選択していた オブジェクトがアクティブならそれを使用する
        activeObj = beforeObj;
      } else {
        // 前回選択していた オブジェクトが非アクティブなら新たに先頭を使用する
        activeObj = activeObjects[0];
        this.firstActiveObjIndex = canvasObjects.indexOf(activeObj);
      }
    } else {
      // 初回の選択の場合、選択したオブジェクトの先頭を使用する
      activeObj = activeObjects[0];
      this.firstActiveObjIndex = canvasObjects.indexOf(activeObj);
    }

    this.isFormatEditable = activeObjects.every(obj => obj.type === 'rect' || obj.type === 'ellipse');

    if (this.isFormatEditable) {
      if (activeObj.fill) {
        this.fillColor = activeObj.fill as string;
        this.isFilled = true;
      } else {
        this.isFilled = false;
      }
    }

    if (this.isBorderEditable) {
      if (activeObj.type === 'text') {
        this.borderColor = activeObj.fill as string;
        this.borderType = (activeObj as fabric.Text).fontSize;
      } else {
        this.borderColor = activeObj.stroke;
        this.borderType = activeObj.strokeWidth;
      }
    }

    this.selectFunction = this.editFunctions[0];

    this.zone.run(() => {
      // レンダリング後に何かしらのイベントを走らせないと値が反映されない
      const event = new KeyboardEvent('keydown', { key: 'Enter' });
      document.getElementById('annotation-main-content').dispatchEvent(event);
    });
  }

  private getFillColor(): string {
    return this.isFilled
      ? this.fillColor
      : null;
  }

  private getOpacityToHex(hex: string): number {
    if (hex.length < 8) {
      return 0;
    }

    // hexの7,8番目を取り出す
    const opacityCode = hex.slice(7, 9);
    const codes = Array.from(this.opacityColorCodes).map(([key, value]) => ({ key, value }));
    const code = codes.find(c => c.value === opacityCode);

    return code.key;
  }

  private toBlob(base64: string, type: string = 'image/jpeg'): Blob {
    // Base64からバイナリへ変換
    const bin = atob(base64.replace(/^.*,/, ''));
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    // Blobを作成
    return new Blob([buffer.buffer], { type: type });
  }

  private setBrightness(img: fabric.Image, brightness: number) {
    const filters: IBaseFilter[] = [];
    const filter = new fabric.Image.filters.Brightness({ brightness: brightness });
    filters.push(filter); // 明度プロパティの設定
    img.filters = filters;
    img.applyFilters(); // 明度プロパティの適用
  }

  private setCanvasAngle() {
    const mainImage = this.canvas.getObjects().find(obj => obj.type === 'image') as fabric.Image;
    const dummyImage = this.dummyCanvas.getObjects().find(obj => obj.type === 'image') as fabric.Image;
    const angleRemainder = Math.abs(mainImage.angle % 180);
    const scale = this.getScale();

    let mainCanvasWidth: number;
    let mainCanvasHeight: number;
    let dummyCanvasWidth: number;
    let dummyCanvasHeight: number;
    if (angleRemainder === 0) {
      mainCanvasWidth = this.baseImageWidth * scale;
      mainCanvasHeight = this.baseImageHeight * scale;
      dummyCanvasWidth = dummyImage.width;
      dummyCanvasHeight = dummyImage.height;
    } else {
      mainCanvasWidth = this.baseImageHeight * scale;
      mainCanvasHeight = this.baseImageWidth * scale;
      dummyCanvasWidth = dummyImage.height;
      dummyCanvasHeight = dummyImage.width;
    }

    // キャンバスサイズ変更前と変更後の差分
    const diffX = this.canvas.width - mainCanvasWidth;
    const diffY = this.canvas.height - mainCanvasHeight;

    // キャンバスの回転
    this.canvas.setWidth(mainCanvasWidth);
    this.canvas.setHeight(mainCanvasHeight);
    this.dummyCanvas.setWidth(dummyCanvasWidth);
    this.dummyCanvas.setHeight(dummyCanvasHeight);

    // 画像の開始位置変更 変更差分の分中心座標をずらす
    mainImage.top = mainCanvasHeight / 2;
    mainImage.left = mainCanvasWidth / 2;
    dummyImage.top = dummyCanvasHeight / 2;
    dummyImage.left = dummyCanvasWidth / 2;
    this.renderAll();
  }

  private openFlushConfirmDialog(): MatDialogRef<CommonConfirmDialogComponent> {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: '500px',
      height: '230px',
      data: {
        firstMessage: this.translate.instant('imageAnnotationEdit.openFlushConfirmDialogFirstMessage'),
        secondMessage: '',
        dialogType: 'confirmation',
        trueBtnText: this.translate.instant('imageAnnotationEdit.openFlushConfirmDialogTrueBtnText')
      }
    });
  }

  private openImageTrimDialog(): MatDialogRef<ImageTrimComponent> {
    return this.dialog.open(ImageTrimComponent, {
      panelClass: 'image-trim',
      data: {
        src: this.imgSrcBase64
      }
    });
  }

  private updateMainImageSrc(imgSrc: string, rect: any, angle: number, isFirstLoad: boolean, isRevert: boolean, data: any = null): void {
    this.isCanvasLoading = true;

    const image = new Image();
    image.src = imgSrc;
    this.cropImageSrc = imgSrc;
    image.onload = () => {
      this.baseImageWidth = image.width;
      this.baseImageHeight = image.height;
      this.scale = this.getScale();

      const canvasWidth = image.width * this.scale;
      const canvasHeight = image.height * this.scale;

      const mainBaseImage = this.canvas.getObjects().find(obj => obj.type === 'image') as fabric.Image;
      mainBaseImage.setElement(image);
      mainBaseImage.top = canvasHeight / 2;
      mainBaseImage.left = canvasWidth / 2;
      mainBaseImage.scaleX = mainBaseImage.scaleY = this.scale;
      mainBaseImage.angle = 0;
      mainBaseImage.data = [rect, { isNewSpecification: true }];

      const dummyBaseImage = this.dummyCanvas.getObjects().find(obj => obj.type === 'image') as fabric.Image;
      dummyBaseImage.setElement(image);
      if (isRevert) {
        dummyBaseImage.top = image.height / 2;
        dummyBaseImage.left = image.width / 2;
      } else {
        dummyBaseImage.top = rect.height / 2;
        dummyBaseImage.left = rect.width / 2;
      }
      dummyBaseImage.scaleX = dummyBaseImage.scaleY = 1;
      dummyBaseImage.angle = 0;
      dummyBaseImage.data = [rect, { isNewSpecification: true }];

      this.canvas.setWidth(canvasWidth);
      this.canvas.setHeight(canvasHeight);
      if (isRevert) {
        this.dummyCanvas.setWidth(image.width);
        this.dummyCanvas.setHeight(image.height);
      } else {
        this.dummyCanvas.setWidth(rect.width);
        this.dummyCanvas.setHeight(rect.height);
      }

      this.canvas.renderAll();
      this.dummyCanvas.renderAll();

      if (data) {
        this.rotate(angle, data, isFirstLoad);
      } else if (isRevert) {
        // mainBaseImageのScaleが反映されない為、一度Resize処理を走らせる必要がある
        this.resizeWindow();
      } else {
        this.syncCanvasData(false);
      }
    };
  }

  /** 画像がトリムされたものかどうか判別する
   * @param  {fabric.Image} imageObj
   * @returns boolean
   */
  public isTrimmed(imageObj: fabric.Image = null): boolean {
    if (!this.canvas || this.canvas.getObjects().length < 1) {
      return false;
    }

    if (!imageObj) {
      imageObj = this.canvas.getObjects().find(obj => obj.type === 'image') as fabric.Image;
    }

    return imageObj.data;
  }

  public renderAll() {
    this.canvas.renderAll();
    this.dummyCanvas.renderAll();
  }

  private setupCropCanvas(image: HTMLImageElement): void {
    this.baseImage = image;
    this.baseImageWidth = image.width;
    this.baseImageHeight = image.height;
    let cropImage = new fabric.Image(this.imageSrc);
    this.cropCanvas = new fabric.Canvas('cropCanvas');

    const width = this.TRIM_WIDTH / this.baseImageWidth;
    const height = this.TRIM_HEIGHT / this.baseImageHeight;
    const scale = Math.min(width, height);

    const imgConfig = {
      top: this.TRIM_WIDTH / 2,
      left: this.TRIM_HEIGHT / 2,
      originX: 'center',
      originY: 'center',
      scaleX: scale,
      scaleY: scale
    };

    fabric.Image.fromURL(this.imageSrc, (img) => {
      cropImage = img;
      this.cropCanvas.setWidth(img.width * scale);
      this.cropCanvas.setHeight(img.height * scale);
      this.cropCanvas.add(img);
      this.cropCanvas.centerObject(img);
      this.cropCanvas.add(this.rectangle);
      this.cropCanvas.renderAll();
      this.setupCanvas(image);
    }, imgConfig);
  }

  public crop(angle: number, data: any, isFirstLoad: boolean): any {
    const imageSrc = this.cropCanvas.toDataURL({
      left: this.rectangle.left,
      top: this.rectangle.top,
      width: this.rectangle.width,
      height: this.rectangle.height,
      quality: 1,
      format: 'jpeg'
    });

    this.updateMainImageSrc(imageSrc, this.rectangle, angle, isFirstLoad, false, data);
  }

  public addResizeEvent() {
    window.addEventListener('resize', () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => {
        this.resizeWindow();
      }, 1000);
    });
  }

  public resizeWindow() {
    // Canvasの削除
    // 一度Canvasを削除してから追加しないと、正しくレンダリングされない
    const elem = document.getElementById('main-canvas-area');

    if (elem && elem.lastChild) {
      while (elem.lastChild) {
        elem.removeChild(elem.lastChild);
      }

      // Canvas追加用のDOM追加
      const tag = document.createElement('canvas');
      tag.innerHTML = '<canvas #Canvas></canvas>';
      tag.className = 'main-canvas';
      tag.id = 'canvas';
      elem.appendChild(tag);

      // Canvasサイズの設定
      this.scale = this.getScale();

      const canvasConfig = {
        width: this.baseImageWidth * this.scale,
        height: this.baseImageHeight * this.scale,
        border: 'solid'
      };

      // Canvasの追加
      this.canvas = new fabric.Canvas('canvas', canvasConfig);
      this.canvas.preserveObjectStacking = true; // クリックされた時に図形の順番を変更しない
      this.canvas.freeDrawingBrush.width = this.borderType; // ボーダーのサイズ設定
      this.canvas.freeDrawingBrush.color = this.borderColor; // ボーダーの色設定
      this.canvas.hoverCursor = 'default';
      this.canvas.isDrawingMode = false;
      this.addImage(canvasConfig.width / 2, canvasConfig.height / 2, this.scale, this.cropImageSrc ? this.cropImageSrc : this.baseImage.src, this.MAIN, true);
    }
  }

  public isVerticalImage(): boolean {
    if (!this.canvas) { return; }

    return this.canvas.getHeight() >= this.canvas.getWidth();
  }

  public getScale(): number {
    let scale: number;
    if (this.isEditable) {
      const mainContent = document.getElementById('annotation-main-content');
      const functionArea = document.getElementById('function-area');
      const maxImg = Math.min(this.baseImageWidth, this.baseImageHeight);
      const minContent = Math.min(mainContent.clientWidth - functionArea.clientWidth - 60 - 144, mainContent.clientHeight);
      scale = minContent / maxImg;
    } else {
      const container = document.getElementById('annotation-main-content');
      scale = Math.min(container.clientWidth / this.baseImageWidth, container.clientHeight / this.baseImageHeight) * 0.9;
    }
    return scale;
  }

  public getFuncAreaStyle(): object {
    const style = {};
    const mainContent = document.getElementById('annotation-main-content');
    const canvasArea = document.getElementById('canvas-area');
    const margin = (mainContent.clientHeight - canvasArea.clientHeight) / 2;
    return style;
  }

  public getClearAllStyle(): object {
    const style = {};
    const mainContent = document.getElementById('annotation-main-content');
    const canvasArea = document.getElementById('canvas-area');

    const bottom = (mainContent.clientHeight - canvasArea.clientHeight) / 2;
    // 72: Dataタブの矢印スペース分, 100: clearAllButtonの横幅
    const left = mainContent.clientWidth - 72 - 100;

    style['bottom'] =  bottom.toString() + 'px';
    style['left'] = left.toString() + 'px';
    return style;
  }

  // Scrollbarが出た際の調整
  public getObjectsAreaStyle(): { [key: string]: string } {
    if (this.isScroll()) {
      return { 'width': '44px' };
    } else {
      return {
        'overflow-x': 'clip',
        'overflow-y': 'auto',
        'width': '58px'
      };
    }
  }

  public isScroll(): boolean {

    return 400 <= this.mainContent.clientHeight;
  }

  public getImageSizeScale(): number {
    // 600×600pxの領域に合わせて表示していた時の倍率
    const width = 600 / this.baseImageWidth;
    const height = 600 / this.baseImageHeight;
    return Math.min(width, height);
  }

  // v2.0.19 → v2.0.20でアノテーションの保存倍率が変わった為、保存倍率に応じた倍率を掛けることが必要
  // v2.0.20以降に保存された画像オブジェクトのdata内にはisNewSpecificationフラグを仕込んでいる
  public getScaleCorrectionJsonData(json: any) {
    const jsonData = JSON.parse(JSON.stringify(json));

    if (json.objects[0].data && json.objects[0].data[1] && json.objects[0].data[1].isNewSpecification) {
      return jsonData;
    } else {
      const scale = this.isTrimmed(jsonData.objects[0]) ? 1 / jsonData.objects[0].scaleX : 1 / this.getImageSizeScale();
      jsonData.objects.forEach(obj => {
        if (obj.type === 'image') {
          obj.scaleX = obj.scaleY = 1;
          if (obj.data) {
            obj.data = [obj.data, { isNewSpecification: true }];
          }
        } else {
          obj.scaleX = obj.scaleX * scale;
          obj.scaleY = obj.scaleY * scale;
          obj.left = obj.left * scale;
          obj.top = obj.top * scale;
          obj.fontSize = obj.fontSize * scale;
          obj.x1 = obj.x1 * scale;
          obj.x2 = obj.x2 * scale;
          obj.y1 = obj.y1 * scale;
          obj.y2 = obj.y2 * scale;
        }
      });

      return jsonData;
    }
  }

  clearAllModifiedImage(): void {
    const message = this.translate.instant('imageAnnotationEdit.clearModifiedImageFirstMessage', { fileName: this.inspectionImage.fileName });
    const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
      width: '570px',
      height: '270px',
      data: {
        firstMessage: message,
        secondMessage: '',
        dialogType: 'confirmation',
        trueBtnText: 'OK'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // this.updateMainImageSrc(this.imgSrcBase64, null, 0, true, false);
        this.updateMainImageSrc(this.imgSrcBase64, null, 0, true, true);

        setTimeout(() => {
          this.dummyCanvas._objects = this.dummyCanvas._objects.filter(_ => _.type === 'image');
          const canvasImg = this.dummyCanvas.getObjects().find(_ => _.type === 'image') as any;
          canvasImg.angle = 0;
          canvasImg.filters[0].brightness = 0;
          canvasImg.data = 0;
          canvasImg.left = canvasImg.width / 2;
          canvasImg.top = canvasImg.height / 2;
          canvasImg.applyFilters();

          this.firstActiveObjIndex = -1;
          this.resizeWindow();

          const base64Data = this.dummyCanvas.toDataURL({
            format: 'jpeg',
            quality: 1
          });
        }, 1000);
      }
    });
  }

  isUneditedRetouchImage(): boolean {
    const annotationObjects = this.dummyCanvas?._objects as any;
    if (!annotationObjects) { return true; }

    return annotationObjects.length === 1 && annotationObjects[0].type === 'image'
    && annotationObjects[0].filters[0].brightness === 0 && annotationObjects[0].angle === 0
    && annotationObjects[0].width === this.inspectionImage.originalWidth
    && annotationObjects[0].height === this.inspectionImage.originalHeight;
  }
}
